import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, UsersThree, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { ContactPicker, type PickedContact } from '@/components/shared/ContactPicker';
import { fetchBarristers, type ApiBarrister } from '@/lib/barristersApi';
import {
  fetchMatterRoles,
  updateMatter,
  type MatterRoles,
} from '@/lib/mattersApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  caseId: string;
  caseLabel: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const inputCls =
  'h-9 w-full rounded-md border border-border bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-600/40';
const labelCls = 'block text-xs font-medium text-muted-foreground mb-1.5';

const empty: PickedContact = { name: '', contactId: undefined };

export function AddPartyModal({ caseId, caseLabel, open, onClose, onSuccess }: Props) {
  const [original, setOriginal] = useState<MatterRoles | null>(null);
  const [barristers, setBarristers] = useState<ApiBarrister[]>([]);
  const [loading, setLoading] = useState(false);

  const [layClient, setLayClient] = useState<PickedContact>(empty);
  const [solicitor, setSolicitor] = useState<PickedContact>(empty);
  const [opponent, setOpponent] = useState<PickedContact>(empty);
  const [juniorBarristerId, setJuniorBarristerId] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    Promise.all([fetchMatterRoles(caseId), fetchBarristers()])
      .then(([roles, bs]) => {
        setOriginal(roles);
        setBarristers(bs.filter((b) => b.user.status === 'active'));
        setLayClient({ contactId: roles.layClientId ?? undefined, name: roles.layClientName });
        setSolicitor({
          contactId: roles.instructingSolicitorId ?? undefined,
          name: roles.instructingSolicitorName,
        });
        setOpponent({
          contactId: roles.opponentContactId ?? undefined,
          name: roles.opponentContactName,
        });
        setJuniorBarristerId(roles.juniorBarristerId ?? '');
      })
      .catch((err) => setError(getApiErrorMessage(err, 'Could not load matter roles')))
      .finally(() => setLoading(false));
  }, [open, caseId]);

  useEffect(() => {
    if (open) return;
    setOriginal(null);
    setBarristers([]);
    setLayClient(empty);
    setSolicitor(empty);
    setOpponent(empty);
    setJuniorBarristerId('');
    setError(null);
  }, [open]);

  if (!open) return null;

  // Build a delta of what actually changed since the modal opened.
  // Fields that match the original are skipped — the API supports partial PATCH.
  //
  // A picked EXISTING contact → send its id (link). A typed NEW name (no contactId)
  // → send a party object so the API creates a real Contact via the same canonical
  // flow used at matter creation — it then appears on the Clients page. This keeps
  // "add a person on an existing matter" identical to "add a person while creating".
  function buildDelta(): Record<string, unknown> | null {
    if (!original) return null;
    const delta: Record<string, unknown> = {};

    // Lay client → partyA
    if (layClient.contactId) {
      if (layClient.contactId !== original.layClientId) delta.layClientId = layClient.contactId;
    } else if (layClient.name.trim() && layClient.name.trim() !== original.layClientName) {
      delta.partyA = { name: layClient.name.trim(), role: 'claimant' };
    }

    // Instructing solicitor → instructingSolicitor object (creates a 'solicitor' Contact)
    if (solicitor.contactId) {
      if (solicitor.contactId !== original.instructingSolicitorId) delta.instructingSolicitorId = solicitor.contactId;
    } else if (solicitor.name.trim() && solicitor.name.trim() !== original.instructingSolicitorName) {
      delta.instructingSolicitor = { name: solicitor.name.trim() };
    }

    // Opponent → partyB
    if (opponent.contactId) {
      if (opponent.contactId !== original.opponentContactId) delta.opponentContactId = opponent.contactId;
    } else if (opponent.name.trim() && opponent.name.trim() !== original.opponentContactName) {
      delta.partyB = { name: opponent.name.trim(), role: 'defendant' };
    }

    const junior = juniorBarristerId || null;
    if (junior !== original.juniorBarristerId) delta.juniorBarristerId = junior;

    return delta;
  }

  const canSubmit = !loading && !submitting && original !== null;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const delta = buildDelta();
    if (!delta || Object.keys(delta).length === 0) {
      toast.info('No changes to save');
      onClose();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Say it worked only when the server says so. The write is optimistic and the store
      // handles its own failure, so this modal's catch below can never fire for a REFUSAL —
      // the success toast appeared next to the store's red one and the change was already
      // rolled back. Same fault as the matter edit dialogs.
      // The dialog stays open on a refusal — the store has already said why — so the user can
      // adjust and retry rather than lose what they typed. `finally` clears the busy state.
      if (!(await updateMatter(caseId, delta as Parameters<typeof updateMatter>[1]))) return;
      toast.success('Case roles updated');
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not update the matter'));
    } finally {
      setSubmitting(false);
    }
  };

  // A typed-but-not-picked name (no contactId) is a NEW contact — the API creates it
  // on save and it appears on the Clients page. Reassure the user rather than block them.
  function pickerHint(p: PickedContact): string | null {
    if (!p.name.trim()) return null;
    if (p.contactId) return null;
    return 'New contact — will be created and added to your Clients page on save.';
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 sm:items-center">
      <ScrollLock />
      <div className="w-full max-w-lg rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <UsersThree className="h-4 w-4 text-brand-600 dark:text-brand-400" />
            <div>
              <div className="text-sm font-semibold text-foreground">Update case roles</div>
              <div className="text-xs text-muted-foreground">{caseLabel}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Loading current roles…
            </div>
          ) : (
            <>
              <ContactPicker
                label="Lay client"
                placeholder="Search lay client…"
                filterType="lay_client"
                value={layClient}
                onChange={setLayClient}
              />
              {pickerHint(layClient) && (
                <div className="-mt-2 text-xs text-emerald-700 dark:text-emerald-400">{pickerHint(layClient)}</div>
              )}

              <ContactPicker
                label="Instructing solicitor"
                placeholder="Search solicitor…"
                filterType="solicitor"
                value={solicitor}
                onChange={setSolicitor}
              />
              {pickerHint(solicitor) && (
                <div className="-mt-2 text-xs text-emerald-700 dark:text-emerald-400">{pickerHint(solicitor)}</div>
              )}

              <ContactPicker
                label="Opponent"
                placeholder="Search opposing party…"
                filterType="firm"
                value={opponent}
                onChange={setOpponent}
              />
              {pickerHint(opponent) && (
                <div className="-mt-2 text-xs text-emerald-700 dark:text-emerald-400">{pickerHint(opponent)}</div>
              )}

              <div>
                <label className={labelCls}>Junior barrister</label>
                <select
                  className={inputCls}
                  value={juniorBarristerId}
                  onChange={(e) => setJuniorBarristerId(e.target.value)}
                >
                  <option value="">— None —</option>
                  {barristers.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.fullName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                <strong className="text-foreground">Note:</strong> the lead barrister is set
                via the brief-handshake flow (Offer brief), not here. Witnesses and experts
                will be available once the case-party model is built.
              </div>
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-300">
              <Warning className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
